import React, { useState, useEffect, useContext } from 'react'
import i18n from 'live/services/i18n'
import AdminHeader from 'live/components/header/ui/admin/header'
import EmptyPurgeData from '../components/EmptyPurgeData'
import { PurgeModal } from '../components/PurgeModal'
import styled from 'styled-components'
import Subscriber from 'live/services/subscription'
import PurgeProgressBar from '../components/PurgeProgressBar'
import globalNotification from 'live/services/global-notification'
import PurgeDetails from '../components/PurgeDetails'
import { FormEnabledContext } from 'live/context/form-enabled'
import PurgeRule from '../components/PurgeRule'
import PurgeExceptionRule from '../components/PurgeExceptionRule'
import api from '../api'
import ExtraActionsInjected from '../components/ExtraActionsInjected'
import GlobalAlert from 'live/components/ui/global-alert'

const UX_MINIMUM_TIME_TO_WAIT = 2000

const compare = (first: PurgeItem, second: PurgeItem): number => {
    if (first.eventType < second.eventType) {
        return -1
    }
    if (first.eventType > second.eventType) {
        return 1
    }

    return 0
}

const Purge = (): JSX.Element => {
    const [purgeRules, setPurgeRules] = useState<PurgeRule[]>([])
    const [purgeExceptionRules, setPurgeExceptionRules] = useState<PurgeExceptionRule[]>([])
    const [showModal, setShowModal] = useState(false)
    const [editingRule, setEditingRule] = useState<PurgeRule>(null)
    const [showDetails, setShowDetails] = useState(false)
    const [purgeId, setPurgeId] = useState('')
    const [progress, setProgress] = useState(0)
    const [currentEvent, setCurrentEvent] = useState<PurgeEvent>(null)
    const [availableEventTypes, setAvailableEventTypes] = useState<EventTypeOption[]>([])
    const [loading, setLoading] = useState(true)
    const [alertType, setAlertType] = useState<string>('')
    const [alertMessage, setAlertMessage] = useState<string>('')
    const [showAlert, setShowAlert] = useState<boolean>(false)
    const [showWaitingServerResponse, setShowWaitingServerResponse] = useState<boolean>(false)
    const [waitingServerResponseFor, setWaitingServerResponseFor] = useState<string>('')

    const ALERT_STATUS_SUCCESS = 'success'
    const ALERT_STATUS_ERROR = 'error'

    useEffect(() => {
        const originalTitle = document.title
        document.title = 'Live: ' + i18n('Purge')

        return () => {
            document.title = originalTitle
        }
    }, [])

    useEffect(() => {
        api.getSources()
            .then(response => response.json())
            .then(data =>
                setAvailableEventTypes(
                    data.map(source => {
                        return { label: source.eventType, value: source.eventType }
                    })
                )
            )
    }, [])

    useEffect(() => {
        api.getPurgeDef()
            .then(response => response.json())
            .then(data => {
                setPurgeRules(data.rules.sort(compare))
                setPurgeExceptionRules(data.exceptions.sort(compare))
                setLoading(false)
            })
    }, [])

    const isRunning = (): boolean => {
        return (
            currentEvent?.status === 'RUNNING' ||
            currentEvent?.status === 'WAITING' ||
            currentEvent?.status === 'CANCELING'
        )
    }

    const subscribe = (id: string): void => {
        const subscription = Subscriber({
            url: `/services/plugin-purge/purge/purge-events/${id}`
        })

        subscription.on('event', ([event]: PurgeEvent[]) => {
            if (event.finished) {
                subscription.close(null, false)
            }
            const currentProgress = Math.floor((event.progress / event.total) * 100)
            event.definition.rules = event.definition.rules.sort(compare)
            setCurrentEvent(prevState => {
                if (prevState?.purgeId === event.purgeId && prevState.finished) {
                    return prevState
                }

                return event
            })
            setProgress(currentProgress)
        })

        subscription.flush(null)
    }

    const simulateTask = (): void => {
        setWaitingServerResponseFor(i18n('Simulate'))
        setShowWaitingServerResponse(true)
        setProgress(0)
        api.simulateTask()
            .then(response => response.text())
            .then(id => {
                setPurgeId(id)
                subscribe(id)
            })
            .finally(() => {
                setTimeout(() => setShowWaitingServerResponse(false), UX_MINIMUM_TIME_TO_WAIT)
            })
    }

    const runTask = (): void => {
        setWaitingServerResponseFor(i18n('Purge'))
        setShowWaitingServerResponse(true)
        setProgress(0)
        api.runTask()
            .then(response => response.text())
            .then(id => {
                setPurgeId(id)
                subscribe(id)
            })
            .finally(() => {
                setTimeout(() => setShowWaitingServerResponse(false), UX_MINIMUM_TIME_TO_WAIT)
            })
    }

    const cancelTask = (): void => {
        api.cancelTask(purgeId)
    }

    const removePurgeRule = (purgeRule: PurgeRule): void => {
        api.removePurgeRule(purgeRule).then(response => {
            if (response.ok) {
                globalNotification.success(i18n('Purge rule successfully removed'))
                setPurgeRules(prevState => prevState.filter(prevRule => prevRule !== purgeRule))
            } else {
                globalNotification.error(i18n('Failed to remove the purge rule'))
            }
        })
    }

    const removePurgeExceptionRule = (purgeExceptionRule: PurgeExceptionRule): void => {
        api.removePurgeExceptionRule(purgeExceptionRule).then(response => {
            if (response.ok) {
                globalNotification.success(i18n('Purge exception successfully removed'))
                setPurgeExceptionRules(prevState =>
                    prevState.filter(prevException => prevException !== purgeExceptionRule)
                )
            } else {
                globalNotification.error(i18n('Failed to remove the purge exception'))
            }
        })
    }

    const toggleModal = (editingPurgeRule: PurgeRule | null = null): void => {
        setShowModal(prevState => !prevState)
        if (editingPurgeRule) {
            setEditingRule(editingPurgeRule)
        } else {
            setEditingRule(null)
        }
    }

    const context: { readonly: boolean } = useContext(FormEnabledContext)
    const readonly = context.readonly || showWaitingServerResponse

    function alert(success: boolean, message: string): void {
        if (success) {
            setAlertMessage(message)
            setAlertType(ALERT_STATUS_SUCCESS)
        } else {
            setAlertMessage(message)
            setAlertType(ALERT_STATUS_ERROR)
        }
        setShowAlert(true)
    }

    function loadPurgeRules(): void {
        setLoading(true)
        api.getPurgeDef()
            .then(response => response.json())
            .then(data => {
                setPurgeRules(data.rules.sort(compare))
                setPurgeExceptionRules(data.exceptions.sort(compare))
                setLoading(false)
            })
    }

    function showAlertAndLoadPurgeRules(success: boolean, message: string): void {
        alert(success, message)
        loadPurgeRules()
    }

    return (
        <div className={'admin-content'} style={{ marginLeft: '215px' }}>
            <AdminHeader>
                <h2>
                    <span>{i18n('Purge')}</span>
                </h2>
            </AdminHeader>
            <div className={`admin-body admin-plugin ${loading && 'loading'}`}>
                <Container>
                    {purgeRules.length === 0 && purgeExceptionRules.length === 0 ? (
                        <>
                            <ButtonContainer>
                                <ExtraActionsInjected
                                    renderSeparator={false}
                                    afterActionExecute={showAlertAndLoadPurgeRules}
                                />
                            </ButtonContainer>
                            <EmptyPurgeData
                                title={i18n('There are no purge rules configured.')}
                                subtitle={i18n('You can add a new rule to test and run a one-time data purge event.')}
                            />
                        </>
                    ) : (
                        <>
                            <GlobalAlert
                                show={showWaitingServerResponse}
                                neverClose={false}
                                allowClose={false}
                                visibleTime={0}
                                message={i18n('PerformPurgeAction', { kind: waitingServerResponseFor })}
                                className={`alert-sm info`}
                            />
                            <ButtonContainer>
                                {!isRunning() ? (
                                    <>
                                        <SimulatePurgeButton onClick={simulateTask} disabled={readonly}>
                                            <i className="fa fa-play-circle-o" /> {i18n('Simulate')}
                                        </SimulatePurgeButton>
                                        <RunPurgeButton onClick={runTask} disabled={readonly}>
                                            <i className="fa fa-play" /> {i18n('Run purge')}
                                        </RunPurgeButton>
                                    </>
                                ) : (
                                    <CancelButton
                                        onClick={cancelTask}
                                        disabled={readonly || currentEvent?.status === 'CANCELING'}
                                    >
                                        <i className="fa fa-stop" /> {i18n('Cancel')}
                                    </CancelButton>
                                )}
                                <ExtraActionsInjected
                                    readonly={readonly}
                                    renderSeparator={true}
                                    afterActionExecute={showAlertAndLoadPurgeRules}
                                />
                            </ButtonContainer>
                            {currentEvent && (
                                <PurgeProgressBar
                                    now={progress}
                                    status={currentEvent.status}
                                    duration={currentEvent.duration}
                                    eventCount={currentEvent.eventCount}
                                    showDetails={() => setShowDetails(true)}
                                />
                            )}
                            <Table>
                                <thead>
                                    <tr>
                                        <td>{i18n('Recurrence')}</td>
                                        <td>{i18n('Action')}</td>
                                        <td>{i18n('Event type')}</td>
                                        <td>{i18n('Time range')}</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {purgeRules.map(purgeRule => {
                                        return (
                                            <TableRow key={`${purgeRule.eventType}-${purgeRule.timeRange}`}>
                                                <PurgeRule
                                                    purgeRule={purgeRule}
                                                    readonly={readonly}
                                                    setEditingRule={toggleModal}
                                                    removePurgeRule={removePurgeRule}
                                                />
                                            </TableRow>
                                        )
                                    })}
                                    {purgeExceptionRules.map(purgeExceptionRule => {
                                        return (
                                            <TableRow key={purgeExceptionRule.eventType}>
                                                <PurgeExceptionRule
                                                    purgeExceptionRule={purgeExceptionRule}
                                                    readonly={readonly}
                                                    setEditingRule={toggleModal}
                                                    removePurgeExceptionRule={removePurgeExceptionRule}
                                                />
                                            </TableRow>
                                        )
                                    })}
                                </tbody>
                            </Table>
                        </>
                    )}
                    <a className={`btn btn-primary ${readonly && 'disabled'}`} onClick={() => setShowModal(true)}>
                        <i className="fa fa-plus-circle fa-fw" /> {i18n('Add new rule')}
                    </a>
                    {showModal && (
                        <PurgeModal
                            eventTypeOptions={availableEventTypes}
                            closeModal={() => toggleModal()}
                            setPurgeRules={setPurgeRules}
                            setPurgeExceptionRules={setPurgeExceptionRules}
                            oldPurgeRule={editingRule || null}
                        />
                    )}
                    {showDetails && (
                        <PurgeDetails currentEvent={currentEvent} closeDetails={() => setShowDetails(false)} />
                    )}
                </Container>
                <GlobalAlert
                    show={showAlert}
                    neverClose={false}
                    allowClose={true}
                    message={i18n(alertMessage)}
                    visibleTime="default"
                    className={`alert-sm ${alertType}`}
                    onClose={() => setShowAlert(false)}
                />
            </div>
        </div>
    )
}

const Container = styled.div`
    margin: 40px 50px 0px 50px;
    display: flex;
    flex-direction: column;
`

const ButtonContainer = styled.div`
    display: flex;
    margin-bottom: 20px;
`

const ActionButton = styled.button<{ disabled: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px 24px;
    gap: 8px;

    height: 40px;

    border: none;
    border-radius: 3px;
    font-size: 16px;
    font-weight: 600;

    flex: none;
    order: 0;
    flex-grow: 0;
`

const SimulatePurgeButton = styled(ActionButton)`
    background: var(--gray-200);
    color: ${({ disabled }) => (disabled ? 'var(--gray-700)' : 'var(--blue-700)')};
    margin-right: 16px;
`

const RunPurgeButton = styled(ActionButton)`
    background: var(--gray-200);
    color: ${({ disabled }) => (disabled ? 'var(--gray-700)' : 'var(--blue-700)')};
`

const CancelButton = styled(ActionButton)`
    background: ${({ disabled }) => (disabled ? 'var(--gray-200)' : 'var(--red-500)')};
    color: ${({ disabled }) => (disabled ? 'var(--gray-700)' : 'var(--gray-0)')};
`

const Table = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0px 10px;
    margin-bottom: 20px;

    thead {
        td {
            border: none;
            padding: 18px 0px 14px;

            font-weight: 600;
            font-size: 12px;
            line-height: 12px;

            padding-left: 24px;
            text-transform: uppercase;
            color: var(--gray-600);
        }
    }
`

const TableRow = styled.tr`
    td {
        border-width: 1px 0 1px 0;
        border-color: var(--gray-400);
        border-style: solid;
        padding: 14px 24px;
        line-height: 16px;

        color: var(--gray-800);
        font-weight: 600;

        :first-child {
            width: 15%;
            font-size: 15px;
            line-height: 16px;
            border-left-width: 1px;
            border-radius: 4px 0 0 4px;

            i {
                margin-right: 6px;
            }
        }

        :nth-child(2) {
            width: 15%;
            font-size: 15px;
            line-height: 16px;

            i {
                margin-right: 6px;
            }
        }

        :nth-child(3) {
            width: 20%;
            font-size: 13px;
        }

        :last-child {
            width: 50%;
            font-size: 13px;
            border-right-width: 1px;
            border-radius: 0 4px 4px 0;
            i {
                font-size: 15px;
                visibility: hidden;
            }
        }
    }

    &:hover {
        td {
            border-color: var(--blue-500);
            :last-child {
                i {
                    visibility: visible;
                }
            }
        }
    }
`

export default Purge
